import React, { useEffect, useState } from "react";
import { Plus, X, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { availabilityApi } from "../../services/availabilityApi";
import { formatApiErrorDetail } from "../../lib/api";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { CancelAvailabilityDialog } from "../../components/availability/CancelAvailabilityDialog";
import { useLocalization } from "../../hooks/useLocalization";

const AVAILABILITY_TYPES = ["AVAILABLE_ALL_DAY", "PREFERRED_TO_WORK", "OPENING", "DAY_SHIFT", "CLOSING", "DOUBLE_SHIFT"];
const EMPTY_FORM = { request_type: "AVAILABILITY", target_month: new Date().toISOString().slice(0, 7), date: "", availability_type: "AVAILABLE_ALL_DAY", notes: "" };

export default function MyAvailabilityPage() {
  const { t } = useLocalization();
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);

  const load = () => availabilityApi.myRequests().then((r) => setRequests(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm(EMPTY_FORM); setEditingId(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        request_type: form.request_type,
        target_month: form.target_month,
        date: form.date || null,
        availability_type: form.request_type === "AVAILABILITY" ? form.availability_type : null,
        notes: form.notes || null,
      };
      if (editingId) {
        await availabilityApi.amend(editingId, payload);
        toast.success(t("myAvailability.requestUpdated"));
      } else {
        await availabilityApi.submit(payload);
        toast.success(t("myAvailability.requestSubmitted"));
      }
      resetForm();
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleCancel = async () => {
    try {
      await availabilityApi.cancel(cancelTarget.id);
      toast.success(t("myAvailability.requestCancelled"));
      setCancelTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleEdit = (req) => {
    setEditingId(req.id);
    setForm({
      request_type: req.request_type, target_month: req.target_month, date: req.date || "",
      availability_type: req.availability_type || "AVAILABLE_ALL_DAY", notes: req.notes || "",
    });
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="my-availability-page">
      <PageHelpBanner pageKey="my-availability" title={t("pageHelp.defaultTitle")}>
        <p>{t("pageHelp.myAvailability")}</p>
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("myAvailability.title")}</h2>
        <p className="text-sm text-gray-500">{t("myAvailability.subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="availability-form">
        <div className="flex gap-3">
          <select data-testid="request-type-select" value={form.request_type} onChange={(e) => setForm((f) => ({ ...f, request_type: e.target.value }))} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm">
            <option value="AVAILABILITY">{t("myAvailability.requestTypeAvailability")}</option>
            <option value="TIME_OFF">{t("myAvailability.requestTypeTimeOff")}</option>
          </select>
          <input data-testid="target-month-input" type="month" required value={form.target_month} onChange={(e) => setForm((f) => ({ ...f, target_month: e.target.value }))} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm" />
        </div>
        <div className="flex gap-3">
          <input data-testid="availability-date-input" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm" placeholder={t("myAvailability.specificDatePlaceholder")} />
          {form.request_type === "AVAILABILITY" && (
            <select data-testid="availability-type-select" value={form.availability_type} onChange={(e) => setForm((f) => ({ ...f, availability_type: e.target.value }))} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm">
              {AVAILABILITY_TYPES.map((tp) => <option key={tp} value={tp}>{t(`myAvailability.${tp}`)}</option>)}
            </select>
          )}
        </div>
        <textarea data-testid="availability-notes-input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder={t("myAvailability.notePlaceholder")} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" rows={2} />
        {error && <p className="text-sm text-red-500" data-testid="availability-form-error">{error}</p>}
        <div className="flex gap-2">
          <button data-testid="submit-availability-button" type="submit" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all">
            <Plus size={15} /> {editingId ? t("myAvailability.saveAmendment") : t("myAvailability.submit")}
          </button>
          {editingId && <button type="button" onClick={resetForm} className="px-4 py-2 rounded-full border border-gray-300 text-sm text-gray-600 hover:bg-gray-100">{t("myAvailability.cancelEdit")}</button>}
        </div>
      </form>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("myAvailability.colType")}</th>
              <th className="px-4 py-3 font-medium">{t("myAvailability.colMonth")}</th>
              <th className="px-4 py-3 font-medium">{t("myAvailability.colDate")}</th>
              <th className="px-4 py-3 font-medium">{t("myAvailability.colDetail")}</th>
              <th className="px-4 py-3 font-medium">{t("common.status")}</th>
              <th className="px-4 py-3 font-medium">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} className="border-t border-gray-100" data-testid={`availability-row-${r.id}`}>
                <td className="px-4 py-3">{r.request_type === "TIME_OFF" ? t("myAvailability.dayOff") : t("myAvailability.availability")}</td>
                <td className="px-4 py-3">{r.target_month}</td>
                <td className="px-4 py-3">{r.date || "-"}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{(r.availability_type && t(`myAvailability.${r.availability_type}`)) || r.notes || "-"}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3">
                  {["DRAFT", "SUBMITTED"].includes(r.status) && (
                    <div className="flex items-center gap-2">
                      <button data-testid={`edit-availability-${r.id}`} onClick={() => handleEdit(r)} className="text-blue-600 hover:text-blue-800"><Edit2 size={14} /></button>
                      <button data-testid={`cancel-availability-${r.id}`} onClick={() => setCancelTarget(r)} className="text-red-500 hover:text-red-700"><X size={14} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {requests.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500" data-testid="no-availability-requests">{t("myAvailability.noSubmissions")}</td></tr>}
          </tbody>
        </table>
      </div>
      <CancelAvailabilityDialog open={!!cancelTarget} onOpenChange={(next) => !next && setCancelTarget(null)} request={cancelTarget} onConfirm={handleCancel} />
    </div>
  );
}
